// src/components/dashboard/TestingButton.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Sparkles, Star, Zap, Award, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { recordTestingEntry, hasTestedToday } from '@/lib/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

interface TestingButtonProps {
  onSuccess: () => void | Promise<void>;
  hasTested: boolean;
}

const celebrationEmojis = ['🎉', '🎊', '✨', '⭐', '🌟', '💫', '🎯', '🔥', '⚡', '🏆', '👏', '🎈'];

export const TestingButton: React.FC<TestingButtonProps> = ({ onSuccess, hasTested: initialHasTested }) => {
  const { user } = useAuth();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [loading, setLoading] = useState(false);
  const [hasTested, setHasTested] = useState(initialHasTested);
  const [showCelebration, setShowCelebration] = useState(false);

  // Sync internal state with prop when it changes
  useEffect(() => {
    console.log('📊 TestingButton: hasTested prop changed to:', initialHasTested);
    setHasTested(initialHasTested);
  }, [initialHasTested]);

  // Double-check if user has tested today on mount
  useEffect(() => {
    const checkTestingStatus = async () => {
      if (user?.uid) {
        const tested = await hasTestedToday(user.uid);
        console.log('🔍 TestingButton: DB check - hasTested:', tested);
        setHasTested(tested);
      }
    };
    checkTestingStatus();
  }, [user?.uid]);

  const handleTestingSubmit = async () => {
    // Prevent duplicate submissions
    if (!user || hasTested || loading) {
      console.log('⚠️ Test submission blocked:', { user: !!user, hasTested, loading });
      return;
    }

    setLoading(true);
    console.log('📝 Recording test from button...');
    
    try {
      await recordTestingEntry(user.uid, user.displayName, user.photoURL);
      console.log('✅ Test recorded successfully from button!');
      
      // Update local state immediately
      setHasTested(true);
      
      // Only show celebration if user doesn't prefer reduced motion
      if (!prefersReducedMotion) {
        setShowCelebration(true);
        
        // Show celebration for 4 seconds
        setTimeout(() => {
          setShowCelebration(false);
        }, 4000);
      } else {
        // Simple confirmation for reduced motion users
        console.log('✅ Test recorded (reduced motion mode)');
      }
      
      // Call parent success callback to refresh data (await if it's a promise)
      await Promise.resolve(onSuccess());
      console.log('🔄 Parent component refreshed');
    } catch (error) {
      console.error('❌ Error recording testing:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to record testing';
      
      if (errorMessage.includes('Already recorded')) {
        // User already tested - update UI
        setHasTested(true);
        alert('You have already recorded testing for today!');
      } else {
        alert('Failed to record testing. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (hasTested) {
    return (
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative"
      >
        <motion.div
          className="flex flex-col items-center justify-center p-8 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 relative overflow-hidden"
          animate={{
            boxShadow: [
              '0 0 20px rgba(34, 197, 94, 0.3)',
              '0 0 40px rgba(34, 197, 94, 0.5)',
              '0 0 20px rgba(34, 197, 94, 0.3)',
            ],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, ease: 'linear', repeat: Infinity }}
          >
            <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
          </motion.div>
          <h3 className="text-2xl font-bold text-green-500 mb-2">All Done for the Day! 🎉</h3>
          <p className="text-muted-foreground text-lg">Great job! Come back tomorrow to continue your streak</p>
          <div className="mt-3 text-sm text-green-600 font-medium">
            See you tomorrow! 👋
          </div>
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10"
            animate={{
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div className="relative">
      {/* Amazing Celebration Animation */}
      <AnimatePresence>
        {showCelebration && (
          <>
            {/* Ripple Effect */}
            {[0, 1, 2].map((i) => (
              <motion.div
                key={`ripple-${i}`}
                className="absolute inset-0 rounded-2xl border-4 border-yellow-400 pointer-events-none"
                initial={{ scale: 1, opacity: 1 }}
                animate={{ scale: 2.5, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 1.5,
                  delay: i * 0.2,
                  ease: 'easeOut',
                }}
              />
            ))}

            {/* Confetti Explosion - Multiple Layers */}
            <div className="absolute inset-0 pointer-events-none overflow-visible">
              {[...Array(50)].map((_, i) => {
                const emoji = celebrationEmojis[Math.floor(Math.random() * celebrationEmojis.length)];
                const angle = (i / 50) * Math.PI * 2;
                const distance = 150 + Math.random() * 150;
                const x = Math.cos(angle) * distance;
                const y = Math.sin(angle) * distance;

                return (
                  <motion.div
                    key={`confetti-${i}`}
                    className="absolute text-3xl"
                    style={{
                      left: '50%',
                      top: '50%',
                    }}
                    initial={{
                      x: 0,
                      y: 0,
                      scale: 0,
                      rotate: 0,
                      opacity: 1,
                    }}
                    animate={{
                      x: x,
                      y: y,
                      scale: [0, 1.5, 1, 0],
                      rotate: Math.random() * 720 - 360,
                      opacity: [0, 1, 1, 0],
                    }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 2,
                      delay: i * 0.02,
                      ease: 'easeOut',
                    }}
                  >
                    {emoji}
                  </motion.div>
                );
              })}
            </div>

            {/* Sparkle Burst */}
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={`sparkle-${i}`}
                className="absolute"
                style={{
                  left: '50%',
                  top: '50%',
                }}
                initial={{ scale: 0, opacity: 1 }}
                animate={{
                  scale: [0, 1.5, 0],
                  opacity: [1, 1, 0],
                  x: Math.cos((i / 12) * Math.PI * 2) * 100,
                  y: Math.sin((i / 12) * Math.PI * 2) * 100,
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 1,
                  delay: i * 0.05,
                  ease: 'easeOut',
                }}
              >
                <Sparkles className="w-8 h-8 text-yellow-400" />
              </motion.div>
            ))}

            {/* Success Message Popup */}
            <motion.div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none"
              initial={{ scale: 0, opacity: 0, y: 20 }}
              animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 1], y: 0 }}
              exit={{ scale: 0, opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-6 rounded-2xl shadow-2xl border-4 border-white/50">
                <motion.div
                  className="text-center"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 0.5,
                    repeat: 3,
                    ease: 'easeInOut',
                  }}
                >
                  <div className="text-5xl mb-3">🎉</div>
                  <div className="text-3xl font-bold mb-1">Awesome!</div>
                  <div className="text-lg font-medium opacity-95">All Done for Today</div>
                  <div className="text-sm opacity-80 mt-2">Keep up the great work! 🚀</div>
                </motion.div>
              </div>
            </motion.div>

            {/* Firework Effects */}
            {[Trophy, Star, Award, Zap].map((Icon, i) => (
              <motion.div
                key={`icon-${i}`}
                className="absolute left-1/2 top-1/2 pointer-events-none"
                initial={{ scale: 0, opacity: 1 }}
                animate={{
                  scale: [0, 2, 0],
                  opacity: [1, 1, 0],
                  x: [0, Math.cos((i / 4) * Math.PI * 2) * 120],
                  y: [0, Math.sin((i / 4) * Math.PI * 2) * 120],
                  rotate: [0, 180],
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 1.5,
                  delay: 0.3 + i * 0.1,
                  ease: 'easeOut',
                }}
              >
                <Icon className="w-12 h-12 text-yellow-400" />
              </motion.div>
            ))}
          </>
        )}
      </AnimatePresence>

      <motion.div
        whileHover={!hasTested && !loading && !prefersReducedMotion ? { scale: 1.03, y: -5 } : {}}
        whileTap={!hasTested && !loading && !prefersReducedMotion ? { scale: 0.97 } : {}}
        className="relative"
      >
        <Button
          size="lg"
          onClick={handleTestingSubmit}
          disabled={loading || hasTested}
          className={`w-full h-32 text-2xl font-bold shadow-2xl relative overflow-hidden group transition-all ${
            hasTested
              ? 'bg-gradient-to-r from-gray-600 to-gray-700 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
          }`}
        >
          {/* Animated Background Gradient */}
          {!hasTested && !prefersReducedMotion && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 opacity-0 group-hover:opacity-30"
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
              }}
              style={{
                backgroundSize: '200% 200%',
              }}
            />
          )}

          {/* Shimmer Effect */}
          {!hasTested && !prefersReducedMotion && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20"
              animate={{
                x: ['-100%', '200%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          )}

          {/* Button Content */}
          <span className="relative z-10 flex items-center justify-center gap-3">
            {loading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  ⏳
                </motion.div>
                Recording...
              </>
            ) : hasTested ? (
              <>
                <CheckCircle2 className="w-8 h-8" />
                Already Recorded Today
              </>
            ) : (
              <>
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  ✅
                </motion.span>
                I Tested Today! (10+ min)
              </>
            )}
          </span>
        </Button>
      </motion.div>
    </motion.div>
  );
};

